// SettingsPage.js
// Admin-only settings page (accessible via wrench icon in NavPane).
// Owns all data loading and the Practice tab directly.
// All other tabs are self-contained components imported below.
//
// Adding a new tab:
//   1. Create pages/settings/YourNewTab.js, exporting a default component
//      that accepts the data it needs as props plus onRefresh.
//   2. Add the tab label to TABS below.
//   3. Add its data fetch to loadData() if needed.
//   4. Render it in the tab content switch block below.
//   5. Import it at the top.

import React, { useState, useEffect } from 'react';
import { useInactivityTimer } from '../../hooks/useInactivityTimer';
import api from '../../services/api';
import { styles } from './settingsShared';
import Button from '../../components/Button';
import LocationsTab from './LocationsTab';
import UsersTab from './UsersTab';
import TeamsTab from './TeamsTab';
import MedicationsCatalogTab from './MedicationsCatalogTab';
import AllergenCatalogTab from './AllergenCatalogTab';

const TABS = ['Practice', 'Locations', 'Users', 'Provider Teams', 'Medication Catalog', 'Allergen Catalog'];

export default function SettingsPage() {
  useInactivityTimer();

  const [activeTab, setActiveTab] = useState('Practice');
  const [org, setOrg] = useState(null);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const [orgName, setOrgName] = useState('');
  const [orgAbbr, setOrgAbbr] = useState('');
  const [orgEmailDomain, setOrgEmailDomain] = useState('');
  const [trustedDeviceDays, setTrustedDeviceDays] = useState(60);
  const [trustedDeviceLimit, setTrustedDeviceLimit] = useState(7);
  const [notifyNewDevice, setNotifyNewDevice] = useState(false);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [orgRes, locRes, userRes, teamRes] = await Promise.all([
        api.get('/organizations/me'),
        api.get('/locations/'),
        api.get('/users/'),
        api.get('/provider-teams/'),
      ]);
      setOrg(orgRes.data);
      setOrgName(orgRes.data.name);
      setOrgAbbr(orgRes.data.abbreviation || '');
      setOrgEmailDomain(orgRes.data.default_email_domain || '');
      setTrustedDeviceDays(orgRes.data.trusted_device_days ?? 60);
      setTrustedDeviceLimit(orgRes.data.trusted_device_limit ?? 7);
      setNotifyNewDevice(orgRes.data.notify_new_device ?? false);
      setLocations(locRes.data);
      setUsers(userRes.data);
      setTeams(teamRes.data);
    } catch {
      setMessage('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  };

  const savePractice = async () => {
    setSaving(true); setMessage('');
    try {
      const res = await api.patch('/organizations/me', {
        name: orgName,
        abbreviation: orgAbbr,
        default_email_domain: orgEmailDomain.replace(/^@/, '').trim() || null,
        trusted_device_days: trustedDeviceDays,
        trusted_device_limit: trustedDeviceLimit,
        notify_new_device: notifyNewDevice,
      });
      setOrg(res.data);
      setMessage('Practice settings saved.');
    } catch {
      setMessage('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/organizations/me/logo/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setOrg(res.data);
      setMessage(`${type === 'color' ? 'Color' : 'B&W'} logo uploaded.`);
    } catch {
      setMessage('Logo upload failed. Only PNG and SVG are accepted.');
    }
  };

  if (loading) return <div style={styles.loadingCenter}>Loading settings...</div>;

  return (
    <div>
      <h1 style={styles.pageTitle}>Settings</h1>
      <div style={styles.card}>

        {/* Tab bar — plain buttons intentional, tabs are not primary actions */}
        <div style={styles.tabBar}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
            >
              {tab}
            </button>
          ))}
        </div>

        {message && <div style={styles.messageBanner}>{message}</div>}

        <div style={styles.tabContent}>

          {/* ── PRACTICE ── */}
          {activeTab === 'Practice' && (
            <div style={practiceStyles.layout}>
              <div style={practiceStyles.fields}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Practice Name</label>
                  <input
                    style={practiceStyles.input}
                    value={orgName}
                    onChange={e => setOrgName(e.target.value)}
                  />
                </div>
                <div style={practiceStyles.row}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Abbreviation</label>
                    <input
                      style={practiceStyles.inputNarrow}
                      value={orgAbbr}
                      onChange={e => setOrgAbbr(e.target.value)}
                      placeholder="e.g. NHMA"
                      maxLength={20}
                    />
                  </div>
                  <div style={{ ...styles.fieldGroup, flex: 1 }}>
                    <label style={styles.label}>Default Email Domain</label>
                    <div style={practiceStyles.emailDomainWrapper}>
                      <span style={practiceStyles.emailDomainPrefix}>@</span>
                      <input
                        style={practiceStyles.emailDomainInput}
                        value={orgEmailDomain}
                        onChange={e => setOrgEmailDomain(e.target.value.replace(/^@/, ''))}
                        placeholder="thekidneydocs.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Security section */}
                <div style={securityStyles.section}>
                  <div style={securityStyles.sectionTitle}>Security</div>
                  <div style={securityStyles.row}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Trusted device duration (days)</label>
                      <input
                        style={securityStyles.numberInput}
                        type="number"
                        min={1}
                        max={365}
                        value={trustedDeviceDays}
                        onChange={e => setTrustedDeviceDays(
                          Math.min(365, Math.max(1, parseInt(e.target.value) || 1))
                        )}
                      />
                      <span style={securityStyles.hint}>1–365 days</span>
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Maximum trusted devices per user</label>
                      <input
                        style={securityStyles.numberInput}
                        type="number"
                        min={1}
                        max={20}
                        value={trustedDeviceLimit}
                        onChange={e => setTrustedDeviceLimit(
                          Math.min(20, Math.max(1, parseInt(e.target.value) || 1))
                        )}
                      />
                      <span style={securityStyles.hint}>1–20 devices</span>
                    </div>
                  </div>
                  <div style={securityStyles.toggleRow}>
                    <div>
                      <div style={styles.label}>Email users when they log in from a new device</div>
                      <div style={securityStyles.toggleHint}>
                        Sends a notification email on every login from an unrecognized device
                      </div>
                    </div>
                    <button
                      style={{
                        ...securityStyles.toggle,
                        backgroundColor: notifyNewDevice ? 'var(--color-nav)' : 'var(--color-border)',
                      }}
                      onClick={() => setNotifyNewDevice(v => !v)}
                      aria-label={notifyNewDevice ? 'Disable new device notifications' : 'Enable new device notifications'}
                    >
                      <div style={{
                        ...securityStyles.toggleKnob,
                        transform: notifyNewDevice ? 'translateX(20px)' : 'translateX(2px)',
                      }} />
                    </button>
                  </div>
                </div>

                <Button
                  onClick={savePractice}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>

              {/* Logo uploads — label wrapper intentional, not a Button */}
              <div style={practiceStyles.logoSection}>
                {[
                  { type: 'color', label: 'Color Logo' },
                  { type: 'bw', label: 'B&W / Grayscale Logo' },
                ].map(({ type, label }) => (
                  <div key={type} style={practiceStyles.logoBlock}>
                    <div style={styles.label}>{label}</div>
                    <div style={practiceStyles.logoPreview}>
                      {(type === 'color' ? org?.logo_color_path : org?.logo_bw_path)
                        ? <span style={practiceStyles.logoUploaded}>✓ Uploaded</span>
                        : <span style={practiceStyles.logoEmpty}>No logo uploaded</span>
                      }
                    </div>
                    <label style={styles.uploadButton}>
                      Upload
                      <input
                        type="file"
                        accept=".png,.svg"
                        style={{ display: 'none' }}
                        onChange={e => handleLogoUpload(e, type)}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── LOCATIONS ── */}
          {activeTab === 'Locations' && (
            <LocationsTab locations={locations} onRefresh={loadData} />
          )}

          {/* ── USERS ── */}
          {activeTab === 'Users' && (
            <UsersTab
              users={users}
              onRefresh={loadData}
              defaultEmailDomain={org?.default_email_domain || ''}
            />
          )}

          {/* ── PROVIDER TEAMS ── */}
          {activeTab === 'Provider Teams' && (
            <TeamsTab teams={teams} users={users} onRefresh={loadData} />
          )}

          {/* ── MEDICATIONS CATALOG ── */}
          {activeTab === 'Medication Catalog' && (
            <MedicationsCatalogTab />
          )}

          {/* ── ALLERGEN CATALOG ── */}
          {activeTab === 'Allergen Catalog' && (
            <AllergenCatalogTab />
          )}

          {/*
            ── FUTURE TABS ──
            When adding a new tab (e.g. 'Lab Ranges', 'Anemia Protocol'):
              {activeTab === 'Lab Ranges' && (
                <LabRangesTab onRefresh={loadData} />
              )}
          */}

        </div>
      </div>
    </div>
  );
}

// ── Practice tab styles ────────────────────────────────────────
const practiceStyles = {
  layout: { display: 'flex', gap: '48px', alignItems: 'flex-start', flexWrap: 'wrap' },
  fields: { display: 'flex', flexDirection: 'column', gap: '16px', width: '480px' },
  row: { display: 'flex', gap: '16px', alignItems: 'flex-end' },
  input: { padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', outline: 'none', width: '100%', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-card)', fontFamily: 'var(--font-family-base)' },
  inputNarrow: { padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', outline: 'none', width: '120px', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-card)', fontFamily: 'var(--font-family-base)' },
  emailDomainWrapper: { display: 'flex', alignItems: 'center', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--color-bg-card)', overflow: 'hidden' },
  emailDomainPrefix: { padding: '8px 8px 8px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-subtle)', borderRight: '1px solid var(--color-border)', fontFamily: 'var(--font-family-base)' },
  emailDomainInput: { padding: '8px 10px', border: 'none', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', outline: 'none', flex: 1, backgroundColor: 'var(--color-bg-card)', fontFamily: 'var(--font-family-base)' },
  logoSection: { display: 'flex', gap: '24px' },
  logoBlock: { display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center', width: '140px' },
  logoPreview: { width: '120px', height: '80px', border: '1px dashed var(--color-border)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoUploaded: { fontSize: 'var(--font-size-xs)', color: '#059669' },
  logoEmpty: { fontSize: '11px', color: 'var(--color-text-placeholder)', textAlign: 'center', padding: '4px' },
};

const securityStyles = {
  section: { borderTop: '1px solid var(--color-border)', paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
  sectionTitle: { fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-family-base)' },
  row: { display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' },
  numberInput: { padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', outline: 'none', width: '100px', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-card)', fontFamily: 'var(--font-family-base)' },
  hint: { fontSize: '11px', color: 'var(--color-text-placeholder)', marginTop: '4px', display: 'block', fontFamily: 'var(--font-family-base)' },
  toggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' },
  toggleHint: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: '2px', fontFamily: 'var(--font-family-base)' },
  toggle: { flexShrink: 0, width: '44px', height: '24px', borderRadius: 'var(--radius-full)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background-color var(--transition-normal)', padding: 0 },
  toggleKnob: { position: 'absolute', top: '2px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#ffffff', transition: 'transform var(--transition-normal)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },
};